import React from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {getDefinedValue, px} from '../../../utils';

import {ModalCardView} from './ModalCard.styles';

import type {CardElevation} from '../../Card/Card';
import type {HTMLAttributes, SpringTooling} from '../../__SharedTypes';

export type ModalCardProps = {
  /**
   * Contents of the Modal.
   */
  children?: React.ReactNode;
  /**
   * Determines how much shadow the Modal card has.
   */
  elevation?: CardElevation;
  /**
   * Increases the border-radius on the Modal card when `true`.
   */
  hasSoftCorners?: boolean;
  /**
   * A custom height for the Modal. For finer control of the Modal's sizing, consider combining the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  height?: number | string;
  isCentered?: boolean;
  isFullHeight?: boolean;
  /**
   * Stretches the Modal to the full height and width of the screen.
   *
   * Will override other `width` and `height` props.
   */
  isFullScreen?: boolean;
  /**
   * A custom max-height for the Modal. For finer control of the Modal's sizing, consider combining the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  maxHeight?: number | string;
  /**
   * A custom max-width for the Modal. For finer control of the Modal's sizing, consider combining the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  maxWidth?: number | string;
  /**
   * A custom min-height for the Modal. For finer control of the Modal's sizing, consider combining the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  minHeight?: number | string;
  /**
   * A custom min-width for the Modal. Use this as the primary way to adjust the custom width of a modal
   * beyond the pre-defined sizes of modalSize.
   *
   * For finer control of the Modal's sizing, consider using the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  minWidth?: number | string;
  /**
   * A preset size (`width`) for the `Modal`. For finer control of the Modal's sizing, consider using the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  modalSize?: 'small' | 'medium' | 'large' | 'x-large';
  /**
   * If the content within the Modal overflows beyond the viewport, you can use
   * the scrollBehavior to control how scrolling should happen.
   *
   * - Inside: Scrolling only occurs within the Modal body.
   * - Outside: Scrolling within the viewport.
   */
  scrollBehavior?: 'inside' | 'outside';
  /**
   * A custom width for the Modal. For general custom width setting, use `width` and refine with `minWidth`
   * and `maxWidth` (if needed). For finer control of the Modal's sizing, consider combining the various
   * sizing properties together (`height`, `minHeight`, `maxHeight`, `minWidth`, `maxWidth`, `width`).
   */
  width?: number | string;
  /**
   * Whether to change cursor to reflect drag state.
   */
  shouldRenderDragHandles?: boolean;
} & HTMLAttributes &
  SpringTooling;

export const ModalCard = React.forwardRef<HTMLDivElement, ModalCardProps>(
  (
    {
      children,
      elevation = 4,
      hasSoftCorners = false,
      height,
      isCentered = false,
      isFullHeight = false,
      isFullScreen,
      maxHeight,
      maxWidth,
      minHeight,
      minWidth,
      modalSize = 'medium',
      shouldRenderDragHandles,
      scrollBehavior = 'inside',
      width: widthProp,
      style = emptyObject,
      ...props
    },
    ref
  ) => {
    const customWidth = px(getDefinedValue(widthProp, '90vw'));
    /**
     * We only want the custom width if any of the width props are defined.
     * Otherwise, we'll send in undefined, allowing for the pre-defined
     * modalSize to take effect.
     *
     * @see https://webflow.atlassian.net/browse/PDTY-3007
     */
    const hasCustomWidth = getDefinedValue(widthProp, false);
    const width = hasCustomWidth ? customWidth : undefined;

    const styles = React.useMemo(
      () => ({
        height: isFullScreen ? '100dvh' : height,
        maxHeight: isFullScreen ? '100dvh' : maxHeight,
        maxWidth: isFullScreen ? '100vw' : maxWidth,
        minHeight: isFullScreen ? '100vh' : minHeight,
        minWidth: isFullScreen ? '100vw' : minWidth,
        width: isFullScreen ? '100vw' : width,
        ...style,
      }),
      [
        isFullScreen,
        height,
        maxHeight,
        maxWidth,
        minHeight,
        minWidth,
        width,
        style,
      ]
    );

    return (
      <ModalCardView
        data-modal-dialog
        elevation={elevation}
        hasSoftCorners={hasSoftCorners}
        isCentered={isCentered}
        isFullHeight={isFullHeight}
        isSeamless
        modalSize={modalSize}
        ref={ref}
        style={styles}
        {...props}
        scrollBehavior={scrollBehavior}
        shouldRenderDragHandles={shouldRenderDragHandles}
      >
        {children}
      </ModalCardView>
    );
  }
);

ModalCard.displayName = 'ModalCard';
ModalCard.toString = ModalCardView.toString;

export default ModalCard;
